import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ReactSlider from "react-slider";
import "../styles/AudioBookPlayer.css";
import Sidebar from "../components/Sidebar.js";
import Header from "../components/Header.js";
import {
  insertBookmark,
  deleteBookMarks,
  fetchNextPageDetails,
} from "../services/AllServices.js";
import {
  speakText,
  conditionalSpeakText,
} from "../components/utils/speechUtils.js";

import { Bookmark } from "lucide-react";

import fastForward from "../images/fast_forward.png";
import fastBackward from "../images/fast_backward.png";
import downArrow from "../images/down_arrow.png";
import skipForward from "../images/skip_forward.png";
import skipBackward from "../images/skip_backward.png";
import playIcon from "../images/playIcon.png";
import pauseIcon from "../images/pauseIcon.png";
import zoomIn from "../images/zoom_in.png";
import zoomOut from "../images/zoom_out.png";
import fullScreen from "../images/fullScreen.png";
import exitFullScreen from "../images/exit_fullscreen.png";
import brightnessImg from "../images/brightness.png";

const AudioBookPlayer = () => {
  const [activeItem, setActiveItem] = useState("dashboard");
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const audioRef = useRef(null);
  const textContainerRef = useRef(null);
  const activeTextRef = useRef(null);

  const [bookData, setBookData] = useState(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [brightness, setBrightness] = useState(100);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [highlightedButton, setHighlightedButton] = useState(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkId, setBookmarkId] = useState(null);
  const [isLoadingBookmark, setIsLoadingBookmark] = useState(false);
  const [lastBookmarkAction, setLastBookmarkAction] = useState(null);
  const dropdownRef = useRef(null);
  const highlightedItemRef = useRef(null);

  // Define currentSection at the top to avoid reference issues
  const currentSection = bookData?.sections?.[currentSectionIndex];

  // Reset bookmark state when section changes
  useEffect(() => {
    if (bookData && currentSection) {
      // Reset to false for every new section - we don't know if it's bookmarked
      // since we don't have a checkBookmark API
      setIsBookmarked(false);
      setBookmarkId(null);
    }
  }, [currentSection, bookData]); // Fixed: Added currentSection to dependency array

  // Initialize book data and start playing
  useEffect(() => {
    if (location.state?.bookData) {
      setBookData(location.state.bookData);
      console.log("wbdudb", location.state.bookData);
      setCurrentSectionIndex(location.state.bookData.SectionIndex || 0);
      setTimeout(() => {
        setIsPlaying(true);
        if (audioRef.current) {
          audioRef.current
            .play()
            .catch((error) => console.warn("Playback failed:", error));
        }
      }, 2000);
    }

    const audioElement = audioRef.current;

    return () => {
      if (audioElement) {
        audioElement.pause();
      }
      window.speechSynthesis.cancel();
    };
  }, [location.state]);

  useEffect(() => {
    const savedSpeed = localStorage.getItem("audioBookSpeed");
    if (savedSpeed) {
      setSpeed(parseFloat(savedSpeed));
    }
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
    bookData?.sections.forEach((section) => {
      if (section.audioRef) {
        section.audioRef.playbackRate = speed;
      }
    });
  }, [speed, bookData]);

  useEffect(() => {
    if (textContainerRef.current) {
      const currentElement =
        textContainerRef.current.querySelector(".highlighted");
      if (currentElement) {
        currentElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [currentSectionIndex]);

  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  const handleSectionEnd = async () => {
    if (currentSectionIndex < (bookData?.sections?.length || 0) - 1) {
      setCurrentSectionIndex((prev) => prev + 1);
      setIsPlaying(true);
      setTimeout(() => audioRef.current?.play(), 100);
    } else {
      if (bookData.PageIndex < bookData.total_pages) {
        const newBookData = await fetchNextPageDetails(
          bookData.bookID,
          bookData.PageIndex,
          "next"
        );
        if (newBookData) {
          setBookData(newBookData.data);
          setCurrentSectionIndex(0);
          setIsPlaying(true);
          setTimeout(() => audioRef.current?.play(), 100);
        } else {
          setIsPlaying(false);
        }
      } else {
        setIsPlaying(false);
        console.log("Reached the end of the book.");
        conditionalSpeakText("You have reached the end of the book", isPlaying);
        setBookData((prevData) => ({
          ...prevData,
          index: bookData.total_pages,
        }));
      }
    }
  };

  const handlePlayPause = useCallback(() => {
    setHighlightedButton("play-pause");
    setTimeout(() => setHighlightedButton(null), 200);

    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        window.speechSynthesis.cancel();

        setTimeout(() => {
          speakText("Audio paused");
        }, 100);
      } else {
        window.speechSynthesis.cancel();

        audioRef.current.play().catch((error) => {
          console.warn("Playback failed:", error);
          setIsPlaying(false);
          speakText("Playback failed");
        });
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);

  const calculatePageProgress = () => {
    if (bookData) {
      if (
        bookData.PageIndex === bookData.total_pages &&
        currentSectionIndex === bookData.sections.length - 1
      ) {
        return 100;
      }
      return ((bookData.PageIndex - 1) / bookData.total_pages) * 100;
    }
    return 0;
  };

  const handleSkipSectionForward = useCallback(() => {
    setHighlightedButton("fast-forward");
    setTimeout(() => setHighlightedButton(null), 200);

    if (currentSectionIndex < bookData?.sections?.length - 1) {
      setCurrentSectionIndex((prev) => prev + 1);
      if (!isPlaying) {
      } else {
        setIsPlaying(true);
        setTimeout(() => audioRef.current?.play(), 100);
      }
    }
  }, [currentSectionIndex, bookData, isPlaying]);

  const handleSkipSectionBackward = useCallback(() => {
    setHighlightedButton("fast-backward");
    setTimeout(() => setHighlightedButton(null), 200);

    if (currentSectionIndex > 0) {
      setCurrentSectionIndex((prev) => prev - 1);
      if (!isPlaying) {
      } else {
        setIsPlaying(true);
        setTimeout(() => audioRef.current?.play(), 100);
      }
    }
  }, [currentSectionIndex, isPlaying]);

  const handleSkipPageForward = useCallback(async () => {
    setHighlightedButton("skip-forward");
    setTimeout(() => setHighlightedButton(null), 200);

    if (bookData.PageIndex < bookData.total_pages) {
      const newBookData = await fetchNextPageDetails(
        bookData.bookID,
        bookData.PageIndex,
        "next"
      );
      if (newBookData) {
        setBookData(newBookData.data);
        setCurrentSectionIndex(newBookData.data.SectionIndex || 0);
        if (!isPlaying) {
        } else {
          setIsPlaying(true);
          setTimeout(() => audioRef.current?.play(), 100);
        }
      }
    } else {
      setIsPlaying(false);
      conditionalSpeakText("This is the last page of the book", isPlaying);
    }
  }, [bookData, isPlaying]);

  const handleSkipPageBackward = useCallback(async () => {
    setHighlightedButton("skip-backward");
    setTimeout(() => setHighlightedButton(null), 200);

    if (!bookData || !bookData.bookID) {
      console.error("bookData is undefined or missing bookID");
      return;
    }

    if (bookData.PageIndex > 1) {
      const newBookData = await fetchNextPageDetails(
        bookData.bookID,
        bookData.PageIndex,
        "previous"
      );
      setBookData(newBookData.data);
      setCurrentSectionIndex(newBookData.data.SectionIndex || 0);

      if (!isPlaying) {
      } else {
        setIsPlaying(true);
        setTimeout(() => audioRef.current?.play(), 100);
      }
    }
  }, [bookData, isPlaying]);

  const handlePageChange = useCallback(
    async (pageIndex) => {
      const newBookData = await fetchNextPageDetails(
        bookData.bookID,
        pageIndex,
        "same"
      );
      if (newBookData) {
        setBookData(newBookData.data);
        setCurrentSectionIndex(newBookData.data.SectionIndex || 0);

        if (!isPlaying) {
          conditionalSpeakText(`Moved to page ${pageIndex}`, isPlaying);
        } else {
          setIsPlaying(true);
          setTimeout(() => audioRef.current?.play(), 100);
        }

        setIsDropdownOpen(false);
      }
    },
    [bookData?.bookID, isPlaying]
  );

  const handleSectionClick = (index) => {
    setCurrentSectionIndex(index);

    if (!isPlaying) {
    } else {
      setIsPlaying(true);
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play();
        }
      }, 100);
    }
  };

  const handleBrightnessChange = (value) => {
    setBrightness(value);
    if (!isPlaying) {
      conditionalSpeakText(`Brightness set to ${value} percent`, isPlaying);
    }
  };

  const handleZoomChange = (value) => {
    setZoomLevel(value);
    if (!isPlaying) {
      conditionalSpeakText(`Zoom level set to ${value} percent`, isPlaying);
    }
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
  }, [currentSectionIndex, bookData, speed]);

  const handleSpeedChange = (value) => {
    setSpeed(value);
    localStorage.setItem("audioBookSpeed", value.toString());

    if (audioRef.current) {
      audioRef.current.playbackRate = value;
    }
    if (!isPlaying) {
      conditionalSpeakText(`Speed set to ${value} times`, isPlaying);
    }
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
    const leftSection = document.querySelector(".left-section");
    if (leftSection) {
      leftSection.classList.toggle("fullscreen-active", !isFullScreen);
    }
    if (!isPlaying) {
      conditionalSpeakText(
        isFullScreen ? "Exited full screen mode" : "Entered full screen mode",
        isPlaying
      );
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
    if (!isPlaying && !isDropdownOpen) {
    }
  };

  const handleKeyDown = useCallback(
    (event) => {
      if (event.ctrlKey && event.key === "ArrowDown") {
        event.preventDefault();
        setIsDropdownOpen(true);
        setHighlightedIndex(bookData.PageIndex - 1);
      } else if (event.ctrlKey && event.key === "ArrowUp") {
        event.preventDefault();
        setIsDropdownOpen(false);
      } else if (isDropdownOpen && event.key === "ArrowDown") {
        event.preventDefault();
        const newIndex = Math.min(
          highlightedIndex + 1,
          (bookData?.total_pages || 1) - 1
        );
        setHighlightedIndex(newIndex);
      } else if (isDropdownOpen && event.key === "ArrowUp") {
        event.preventDefault();
        const newIndex = Math.max(highlightedIndex - 1, 0);
        setHighlightedIndex(newIndex);
        conditionalSpeakText(`Page ${newIndex + 1} highlighted`, isPlaying);
      } else if (isDropdownOpen && event.key === "Enter") {
        event.preventDefault();
        handlePageChange(highlightedIndex + 1);
      } else if (event.ctrlKey && event.key === "ArrowRight") {
        if (bookData.PageIndex < bookData.total_pages) {
          event.preventDefault();
          handleSkipPageForward();
        }
      } else if (event.ctrlKey && event.key === "ArrowLeft") {
        if (bookData.PageIndex > 1) {
          event.preventDefault();
          handleSkipPageBackward();
        }
      } else if (event.altKey && event.key === "ArrowRight") {
        if (currentSectionIndex < bookData?.sections?.length - 1) {
          event.preventDefault();
          handleSkipSectionForward();
        }
      } else if (event.altKey && event.key === "ArrowLeft") {
        if (currentSectionIndex > 0) {
          event.preventDefault();
          handleSkipSectionBackward();
        }
      } else if (event.key === " ") {
        event.preventDefault();
        handlePlayPause();
      }
    },
    [
      handlePlayPause,
      handleSkipPageForward,
      handleSkipPageBackward,
      handleSkipSectionForward,
      handleSkipSectionBackward,
      handlePageChange,
      bookData,
      currentSectionIndex,
      isDropdownOpen,
      highlightedIndex,
      isPlaying,
    ]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (
      isDropdownOpen &&
      highlightedIndex !== -1 &&
      highlightedItemRef.current
    ) {
      highlightedItemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [highlightedIndex, isDropdownOpen]);

 const handleSaveBookmark = async () => {
  if (!currentSection) return;

  // Hide tooltip immediately when clicked
  setShowTooltip(false);

  setIsLoadingBookmark(true);
  
  // Add a small delay to ensure audio playback isn't interrupted
  await new Promise(resolve => setTimeout(resolve, 50));
  
  try {
    if (isBookmarked && bookmarkId) {
      console.log("Deleting bookmark with ID:", bookmarkId);
      const response = await deleteBookMarks({
        bookmarkId: bookmarkId,
      });
      if (response.status === "success") {
        setIsBookmarked(false);
        setBookmarkId(null);
        setLastBookmarkAction("remove");
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 1000);
        conditionalSpeakText("Bookmark removed", isPlaying);
        console.log("Bookmark removed successfully!");
      } else {
        console.error("Failed to remove bookmark:", response.message);
        conditionalSpeakText("Failed to remove bookmark", isPlaying);
        // If delete fails, keep the bookmark state as is
      }
    } else {
      const response = await insertBookmark({
        sectionID: currentSection._id,
        index: currentSection.index,
      });
      
      console.log("Bookmark API Response:", response);
      
      if (response.status === "success") {
        // Use the correct field name from the API response
        const newBookmarkId = response.data?.bookMarkID;
        console.log("newbookmarkid", newBookmarkId);
        setIsBookmarked(true);
        setBookmarkId(newBookmarkId);
        setLastBookmarkAction("add");
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 1000);
        conditionalSpeakText("Section added to bookmarks", isPlaying);
        console.log("Bookmark saved successfully! Bookmark ID:", newBookmarkId);
      } else if (response.message && (response.message.includes("Bookmark already exists") || response.message.includes("already exists"))) {
        // Handle the case where bookmark already exists
        // Unfortunately we don't have the bookmark ID to delete it
        setIsBookmarked(true);
        setBookmarkId(null); // We don't know the actual bookmark ID
        setLastBookmarkAction("exists");
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 1000);
        conditionalSpeakText("Bookmark already exists", isPlaying);
        console.log("Bookmark already exists for this section, but we don't have the bookmark ID");
      } else {
        console.error("Failed to save bookmark:", response.message);
        conditionalSpeakText("Failed to save bookmark", isPlaying);
        // Don't change bookmark state if save fails
      }
    }
  } catch (error) {
    console.error("Error handling bookmark:", error);
    conditionalSpeakText("Error handling bookmark", isPlaying);
  } finally {
    setIsLoadingBookmark(false);
  }
};

  if (!bookData) {
    return (
      <div className="loading-container">
        <p>Loading book data...</p>
      </div>
    );
  }

  const resetSearch = () => {
    setSearchQuery("");
  };

  const handleActiveItemChange = (item) => {
    setActiveItem(item);
    if (item === "dashboard") {
      navigate("/dashboard");
    } else if (item === "bookmarks") {
      navigate("/bookmarks");
    } else if (item === "library") {
      navigate("/library");
    } else if (item === "settings") {
      navigate("/settings");
    } else if (item === "help") {
      navigate("/help");
    }

    resetSearch();
  };

  const getHeaderVisibility = () => {
    if (activeItem === "dashboard" || activeItem === "") {
      return {
        showSearch: true,
        showUserProfile: true,
        showArrows: true,
        pageName: "Dashboard",
      };
    } else if (activeItem === "bookmarks") {
      return {
        showSearch: true,
        showUserProfile: true,
        showArrows: true,
        pageName: "",
      };
    } else if (activeItem === "library") {
      return {
        showSearch: false,
        showUserProfile: true,
        showArrows: true,
        pageName: "My Library",
      };
    } else if (activeItem === "settings") {
      return {
        showSearch: false,
        showUserProfile: true,
        showArrows: true,
        pageName: "Account",
      };
    } else if (activeItem === "help") {
      return {
        showSearch: true,
        showUserProfile: true,
        showArrows: true,
        pageName: "",
      };
    } else {
      return {
        showSearch: false,
        showUserProfile: true,
        showArrows: true,
        pageName: "Search Results",
      };
    }
  };

  const { showSearch, showUserProfile, showArrows, pageName } =
    getHeaderVisibility();

  const handleProfileClick = () => {
    navigate("/settings", { state: { selectedSection: "account" } });
  };

  return (
    <main className="main-content">
      <div className="sidebar_container">
        <Sidebar
          activeItem={activeItem}
          setActiveItem={handleActiveItemChange}
          resetSearch={resetSearch}
          disableSpeech={isPlaying}
        />
      </div>

      <div className="audio_player_container">
        <div className="header_container">
          <Header
            showSearch={showSearch}
            showUserProfile={showUserProfile}
            showArrows={showArrows}
            pageName={pageName}
            searchQuery={searchQuery}
            onSearch={(query) => setSearchQuery(query)}
            onProfileClick={handleProfileClick}
            disableSpeech={isPlaying}
          />
        </div>

        <div className="audio-book-player">
          <div className="left-section">
            <div className="header">
              <div className="arrowSection">
              </div>

              <div className="chapterSection">
                <div
                  className="chapterDropdown"
                  onClick={toggleDropdown}
                  tabIndex={0}
                >
                  <span className="chapterNumber">
                    Page {bookData?.PageIndex}
                  </span>
                  <img src={downArrow} alt="Dropdown" className="icon" />
                  {isDropdownOpen && (
                    <ul className="dropdown-menu" ref={dropdownRef}>
                      {Array.from(
                        { length: bookData?.total_pages || 0 },
                        (_, i) => {
                          const pageNumber = i + 1;
                          return (
                            <li
                              key={pageNumber}
                              ref={
                                highlightedIndex === i
                                  ? highlightedItemRef
                                  : null
                              }
                              className={`dropdown-item ${
                                bookData.PageIndex === pageNumber
                                  ? "selected"
                                  : ""
                              } ${highlightedIndex === i ? "highlighted" : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePageChange(pageNumber);
                              }}
                            >
                              Page {pageNumber}
                            </li>
                          );
                        }
                      )}
                    </ul>
                  )}
                </div>
              </div>

              <div className="saveSection">
                <div
                  className={`book_player_save_icon ${
                    isLoadingBookmark ? "loading" : ""
                  }`}
                  onClick={handleSaveBookmark}
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSaveBookmark();
                    }
                  }}
                  role="button"
                  aria-label={
                    isBookmarked ? "Remove from Bookmarks" : "Save to Bookmarks"
                  }
                >
                  <Bookmark
                    fill={isBookmarked ? "currentColor" : "none"}
                    size={24}
                  />
                  {showTooltip && (
                    <div className="tooltip">
                      {isBookmarked
                        ? "Remove from Bookmarks"
                        : "Save to Bookmarks"}
                    </div>
                  )}
                </div>
                {showSuccessMessage && (
                  <div className={`successMessage ${lastBookmarkAction === "exists" ? "exists-message" : ""}`}>
                    {lastBookmarkAction === "remove"
                      ? "Bookmark removed!"
                      : lastBookmarkAction === "exists"
                      ? "Bookmark already exists!"
                      : "Section added to bookmarks!"}
                  </div>
                )}
              </div>
            </div>

            <div
              className={`book_context_box ${
                isFullScreen ? "fullscreen-active" : ""
              }`}
              ref={textContainerRef}
              style={{ filter: `brightness(${brightness}%)` }}
            >
              <div className="book_content_wrapper">
                {bookData?.sections.map((section, index) => (
                  <p
                    key={index}
                    className={`book_context ${
                      index === currentSectionIndex ? "highlighted" : ""
                    }`}
                    ref={index === currentSectionIndex ? activeTextRef : null}
                    onClick={() => handleSectionClick(index)}
                    style={{
                      fontSize: `${zoomLevel * 0.02}vw`,
                      color:
                        index === currentSectionIndex
                          ? "var(--text-primary)"
                          : "var(--text-grey)",
                    }}
                  >
                    {section.text}
                  </p>
                ))}
              </div>

              <div
                className="fullscreen"
                onClick={toggleFullScreen}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    toggleFullScreen();
                  }
                }}
                role="button"
                aria-label={
                  isFullScreen ? "Exit full screen" : "Enter full screen"
                }
              >
                <img
                  src={isFullScreen ? exitFullScreen : fullScreen}
                  alt={isFullScreen ? "Exit Full Screen" : "Full Screen"}
                />
              </div>
            </div>

            <audio
              ref={audioRef}
              src={bookData?.sections[currentSectionIndex]?.audio}
              onEnded={handleSectionEnd}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              autoPlay
              playbackRate={speed}
            />

            <div className="book-player-controls">
              <div
                className={`control-section skip-backward ${
                  highlightedButton === "skip-backward" ? "highlighted" : ""
                }`}
                onClick={handleSkipPageBackward}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSkipPageBackward();
                  }
                }}
                role="button"
                aria-label="Skip to previous page"
              >
                <img
                  src={skipBackward}
                  alt="Skip Backward"
                  className={`control-icon ${
                    bookData.PageIndex === 1 ? "disabled" : ""
                  }`}
                />
              </div>

              <div
                className={`control-section fast-backward ${
                  highlightedButton === "fast-backward" ? "highlighted" : ""
                }`}
                onClick={
                  currentSectionIndex > 0 ? handleSkipSectionBackward : null
                }
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && currentSectionIndex > 0) {
                    handleSkipSectionBackward();
                  }
                }}
                role="button"
                aria-label="Skip to previous section"
              >
                <img
                  src={fastBackward}
                  alt="Fast Backward"
                  className={`control-icon ${
                    currentSectionIndex === 0 ? "disabled" : ""
                  }`}
                />
              </div>

              <div
                className={`control-section play-pause ${
                  highlightedButton === "play-pause" ? "highlighted" : ""
                }`}
                onClick={handlePlayPause}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handlePlayPause();
                  }
                }}
                role="button"
                aria-label={isPlaying ? "Pause" : "Play"}
              >
                <img
                  src={isPlaying ? pauseIcon : playIcon}
                  alt="Play/Pause"
                  className="control-icon"
                />
              </div>

              <div
                className={`control-section fast-forward ${
                  highlightedButton === "fast-forward" ? "highlighted" : ""
                }`}
                onClick={
                  currentSectionIndex < bookData?.sections?.length - 1
                    ? handleSkipSectionForward
                    : null
                }
                tabIndex={0}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    currentSectionIndex < bookData?.sections?.length - 1
                  ) {
                    handleSkipSectionForward();
                  }
                }}
                role="button"
                aria-label="Skip to next section"
              >
                <img
                  src={fastForward}
                  alt="Fast Forward"
                  className={`control-icon ${
                    currentSectionIndex === bookData?.sections?.length - 1
                      ? "disabled"
                      : ""
                  }`}
                />
              </div>

              <div
                className={`control-section skip-forward ${
                  highlightedButton === "skip-forward" ? "highlighted" : ""
                }`}
                onClick={
                  bookData.PageIndex < bookData.total_pages
                    ? handleSkipPageForward
                    : null
                }
                tabIndex={0}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    bookData.PageIndex < bookData.total_pages
                  ) {
                    handleSkipPageForward();
                  }
                }}
                role="button"
                aria-label="Skip to next page"
              >
                <img
                  src={skipForward}
                  alt="Skip Forward"
                  className={`control-icon ${
                    bookData.PageIndex === bookData.total_pages
                      ? "disabled"
                      : ""
                  }`}
                />
              </div>

              <div className="control-section speed-select-container">
                <select
                  className="speed-select"
                  value={speed}
                  onChange={(e) => handleSpeedChange(Number(e.target.value))}
                  aria-label="Playback speed"
                >
                  <option value={0.5}>0.5x</option>
                  <option value={1}>1x</option>
                  <option value={1.5}>1.5x</option>
                  <option value={2}>2x</option>
                </select>
              </div>
            </div>

            <div className="book-player-progress-bar">
              <input
                type="range"
                min="0"
                max="100"
                value={calculatePageProgress()}
                readOnly
                aria-label="Book progress"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={Math.round(calculatePageProgress())}
              />
              <span className="progress-percent">
                {Math.round(calculatePageProgress())}%
              </span>
            </div>
            <div className="section_out_of">
              Page {bookData.PageIndex} of {bookData.total_pages}
            </div>
          </div>

          <div className="right-section">
            <div className="book-player-info">
              <div className="book-player-cover">
                <img src={bookData.BookDetails.thumbnail} alt="Book Cover" />
              </div>
              <div className="book-player-details">
                <h3>{bookData.BookDetails.title}</h3>
                <h4>{bookData.BookDetails.author_list?.join(", ")}</h4>
              </div>
            </div>

            <div className="book-player-settings">
              <div className="book-player-slider-container">
                <div className="small-img">
                  <img src={brightnessImg} alt="brightness decrease" />
                </div>
                <ReactSlider
                  className="book-player-custom-slider"
                  thumbClassName="book-player-custom-slider-thumb"
                  trackClassName="book-player-custom-slider-track"
                  value={brightness}
                  onChange={handleBrightnessChange}
                  min={80}
                  max={120}
                  ariaLabel="Adjust brightness"
                  ariaValuemin={80}
                  ariaValuemax={120}
                  ariaValuenow={brightness}
                />
                <div className="big-img">
                  <img src={brightnessImg} alt="brightness increase" />
                </div>
              </div>

              <div className="book-player-slider-container">
                <div className="small-img">
                  <img src={zoomOut} alt="Zoom out" />
                </div>
                <ReactSlider
                  className="book-player-custom-slider"
                  thumbClassName="book-player-custom-slider-thumb"
                  trackClassName="book-player-custom-slider-track"
                  value={zoomLevel}
                  min={80}
                  max={120}
                  onChange={handleZoomChange}
                  ariaLabel="Adjust text size"
                  ariaValuemin={80}
                  ariaValuemax={120}
                  ariaValuenow={zoomLevel}
                />
                <div className="big-img">
                  <img src={zoomIn} alt="Zoom in" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default AudioBookPlayer;